import { api, type Page } from "@/lib/api/client";
import type {
  BudgetLine,
  BudgetPayment,
  BudgetSummary,
  EventAttachment,
  EventMaterial,
  EventSession,
  CreateEventInput,
  EventDashboard,
  EventFilePurpose,
  EventFilters,
  EventListRow,
  EventParticipant,
  EventPlan,
  EventPlanHistoryEntry,
  EventRow,
  EventStatus,
  EventTask,
  Physician,
  SponsorshipItem,
  UpdateEventInput,
} from "./events.types";

/** Sponsorluk kalemi gövdesi — tutarlar ondalık DİZE (`"1250.50"`), hiçbir zaman sayı değil. */
export interface SponsorshipItemInput {
  itemType: string;
  description?: string | null;
  currency: string;
  listPrice?: string | null;
  agreedPrice: string;
  quantity: string;
}

export interface BudgetLineInput {
  title: string;
  category: string;
  currency: string;
  plannedAmount: string;
  note?: string | null;
}

export interface BudgetPaymentInput {
  amount: string;
  paidOn: string;
  note?: string;
}

/** `POST /files/upload-intent` yanıtı — baytların gideceği imzalı hedef. */
export interface UploadIntent {
  fileId: string;
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  expiresAt: string;
}

/** `GET /files/purposes` satırı — yuvanın tür ve boyut sınırları SUNUCUDAN okunuyor. */
export interface AttachmentPurposeInfo {
  key: string;
  label: string;
  mimeTypes: string[];
  maxBytes: number;
  maxPerEntity: number;
}

/** `GET /files/{id}/download` yanıtı — kısa ömürlü, `attachment` zorlayan imzalı adres. */
export interface DownloadTarget {
  fileId: string;
  url: string;
  filename: string;
  contentType: string;
  expiresAt: string;
}

export const eventsApi = {
  /** Liste satırı bütçe özetini taşıyor (`budget`, sunucu 7 Ekim 2026) — tek kongre uçları taşımıyor. */
  list(filters: EventFilters): Promise<Page<EventListRow>> {
    return api.page<EventListRow>("/events", { params: { ...filters } });
  },
  get(id: string): Promise<EventRow> {
    return api.get<EventRow>(`/events/${id}`);
  },
  create(input: CreateEventInput): Promise<EventRow> {
    return api.post<EventRow>("/events", input);
  },
  /**
   * KONGRE DÜZENLEME (pz-kongre 6 Ekim 2026, M4/M9/M10) — başlıktaki «Düzenle» formu ve «Stant»
   * sekmesi aynı uca yazıyor. Sürüm zorunlu: iki kişi aynı kongreyi düzenlerse ikincisi 409 alır.
   */
  update(id: string, input: UpdateEventInput): Promise<EventRow> {
    return api.patch<EventRow>(`/events/${id}`, input);
  },
  /**
   * ONAY, DURUM DEĞİŞİKLİĞİNDEN AYRI UÇ — ve ayrı anahtar (`events.event.approve`).
   *
   * Kullanıcı kararı, 16 Eylül 2026: «bunun onayı müdüre gidiyor ilk, patron da süreci
   * görebiliyor.» Öncesinde `PLANNED → APPROVED` de `changeStatus`'tan geçiyordu ve kapısı
   * `events.event.manage` idi — yani kongreyi AÇAN kişi kendi kongresini onaylıyordu.
   *
   * Onaylayan ve tarih SUNUCUDA yazılıyor, bu gövdede yok: istemcinin söylediği bir onaylayan,
   * imzalayanla kaydedilenin farklı olabilmesi demek.
   */
  /**
   * KONGRE ONAYI MOTORDA (1 Kasım 2026): «Onaya gönder» zinciri açar (birim başı → patron); «Onayla» ve
   * «Reddet» okuyanın bu kongredeki AÇIK görevine karar. Üçü de gövdesiz cevap (204) — kart yeniden okunuyor.
   */
  submit(id: string): Promise<void> {
    return api.post<void>(`/events/${id}/submit`, {});
  },
  approve(id: string, input: { statusNote?: string; version: number }): Promise<void> {
    return api.post<void>(`/events/${id}/approve`, input);
  },
  reject(id: string, input: { reason: string }): Promise<void> {
    return api.post<void>(`/events/${id}/reject`, input);
  },
  /**
   * Durum değişikliği AYRI uç: iptal gerekçe istiyor, bir alan düzeltmesi değil.
   *
   * ONAY BURADAN GEÇMİYOR ARTIK — `APPROVED` hedefi sunucuda reddediliyor.
   */
  changeStatus(id: string, input: { status: EventStatus; statusNote?: string; version: number }) {
    return api.post<EventRow>(`/events/${id}/status`, input);
  },
  dashboard(params: { legalEntityId?: string; year?: number }): Promise<EventDashboard> {
    return api.get<EventDashboard>("/events/dashboard", { params });
  },

  /* ---------------------------------------------------------- sponsorluk */

  sponsorshipItems(eventId: string): Promise<SponsorshipItem[]> {
    return api.get<SponsorshipItem[]>(`/events/${eventId}/sponsorship-items`);
  },
  createSponsorshipItem(eventId: string, input: SponsorshipItemInput): Promise<SponsorshipItem> {
    return api.post<SponsorshipItem>(`/events/${eventId}/sponsorship-items`, input);
  },
  updateSponsorshipItem(
    id: string,
    input: Partial<SponsorshipItemInput> & { version: number },
  ): Promise<SponsorshipItem> {
    return api.patch<SponsorshipItem>(`/events/sponsorship-items/${id}`, input);
  },
  /** Sürüm SORGU DİZESİNDE: `api.delete` gövde taşımıyor, uç `version`'ı zorunlu istiyor. */
  removeSponsorshipItem(id: string, version: number): Promise<void> {
    return api.delete(`/events/sponsorship-items/${id}`, { params: { version: String(version) } });
  },

  /* --------------------------------------------------- bütçe kalemi + ödeme */

  budgetSummary(eventId: string): Promise<BudgetSummary> {
    return api.get<BudgetSummary>(`/events/${eventId}/budget-summary`);
  },
  budgetLines(eventId: string): Promise<BudgetLine[]> {
    return api.get<BudgetLine[]>(`/events/${eventId}/budget-lines`);
  },
  /**
   * TEKRARA KARŞI KORUMALI: uç `Idempotency-Key` istiyor. Anahtar çağıranın `useRef`'inde,
   * yalnız BAŞARIDAN sonra yenileniyor — zaman aşımından sonraki yeniden deneme aynı anahtarla
   * gider ve ikinci bir kalem açılmaz. Yanıt `reference` olabilir (yalnız kimlik): ekran listeyi
   * zaten yeniden okuyor.
   */
  createBudgetLine(eventId: string, input: BudgetLineInput, idempotencyKey: string): Promise<{ id: string }> {
    return api.post<{ id: string }>(`/events/${eventId}/budget-lines`, input, { idempotencyKey });
  },
  updateBudgetLine(
    id: string,
    input: Partial<BudgetLineInput> & { version: number },
  ): Promise<BudgetLine> {
    return api.patch<BudgetLine>(`/events/budget-lines/${id}`, input);
  },
  /** Ödemesi olan kalem silinmiyor: 409 `BUDGET_LINE_HAS_PAYMENTS`, cümle sunucudan. */
  removeBudgetLine(id: string, version: number): Promise<void> {
    return api.delete(`/events/budget-lines/${id}`, { params: { version: String(version) } });
  },
  budgetPayments(budgetLineId: string): Promise<BudgetPayment[]> {
    return api.get<BudgetPayment[]>(`/events/budget-lines/${budgetLineId}/payments`);
  },
  /**
   * ÖDEME — her durumda (kapalı kongreye geç gelen ödeme de yazılıyor). Para birimi kalemden;
   * gövdede yok. Tekrara karşı korumalı: aynı ödemenin iki kez yazılması «Gerçekleşen»i iki
   * katına çıkarırdı.
   */
  createBudgetPayment(
    budgetLineId: string,
    input: BudgetPaymentInput,
    idempotencyKey: string,
  ): Promise<{ id: string }> {
    return api.post<{ id: string }>(`/events/budget-lines/${budgetLineId}/payments`, input, {
      idempotencyKey,
    });
  },
  removeBudgetPayment(id: string, version: number): Promise<void> {
    return api.delete(`/events/budget-payments/${id}`, { params: { version: String(version) } });
  },

  /* ------------------------------------------------------------- ekler */

  /** Kongrenin ekleri, yuvaya göre — sponsorluk dosyası ya da stant planı. */
  attachments(eventId: string, purpose: EventFilePurpose): Promise<EventAttachment[]> {
    return api.get<EventAttachment[]>(`/events/${eventId}/attachments`, { params: { purpose } });
  },
  /** Taranmış (CLEAN) dosyayı kongreye bağlar; yükleme genel yoldan yapıldı. */
  linkAttachment(
    eventId: string,
    input: { fileId: string; purpose: EventFilePurpose; title?: string },
  ): Promise<EventAttachment> {
    return api.post<EventAttachment>(`/events/${eventId}/attachments`, input);
  },
  removeAttachment(eventId: string, linkId: string): Promise<void> {
    return api.delete(`/events/${eventId}/attachments/${linkId}`);
  },
  paymentAttachments(paymentId: string): Promise<EventAttachment[]> {
    return api.get<EventAttachment[]>(`/events/budget-payments/${paymentId}/attachments`);
  },
  linkPaymentReceipt(paymentId: string, input: { fileId: string; title?: string }): Promise<EventAttachment> {
    return api.post<EventAttachment>(`/events/budget-payments/${paymentId}/attachments`, input);
  },
  removePaymentAttachment(paymentId: string, linkId: string): Promise<void> {
    return api.delete(`/events/budget-payments/${paymentId}/attachments/${linkId}`);
  },

  /* -------------------------------------------- dosya (genel `/files` yolu) */

  /** Yuva kataloğu — tür ve boyut sınırı sunucudan; ekran sabit yazmıyor. */
  attachmentPurposes(entityType: string): Promise<AttachmentPurposeInfo[]> {
    return api.get<AttachmentPurposeInfo[]>("/files/purposes", { params: { entityType } });
  },
  uploadIntent(input: {
    filename: string;
    contentType: string;
    byteSize: number;
    purpose: string;
    entityType: string;
    entityId: string;
  }): Promise<UploadIntent> {
    return api.post<UploadIntent>("/files/upload-intent", input);
  },
  completeUpload(fileId: string): Promise<unknown> {
    return api.post(`/files/${fileId}/complete`, {});
  },
  /** İmzalı indirme adresi — erişim bağın öznesinden (kongre / ödeme) yeniden soruluyor. */
  downloadUrl(fileId: string): Promise<DownloadTarget> {
    return api.get<DownloadTarget>(`/files/${encodeURIComponent(fileId)}/download`);
  },
  /** Baytlar `inline` — yeni sekmede açmak için (PDF ve görsel). */
  content(fileId: string, accept: string): Promise<Blob> {
    return api.getBlob(`/files/${encodeURIComponent(fileId)}/content`, accept);
  },

  /* ------------------------------------------------------------------- program */

  sessions(eventId: string): Promise<EventSession[]> {
    return api.get<EventSession[]>(`/events/${eventId}/sessions`);
  },
  createSession(
    eventId: string,
    input: {
      topic: string;
      room?: string;
      startsAt?: string;
      endsAt?: string;
      attendeeCount?: number;
      note?: string;
    },
  ): Promise<EventSession> {
    return api.post<EventSession>(`/events/${eventId}/sessions`, input);
  },
  /**
   * OTURUM DÜZELTMESİ — konu, salon, gün ve saat (açık kongrede) ya da dinleyici sayısı ve not
   * (her durumda). Kapalı kongrede yalnız ikincisi kabul ediliyor (M5: «dinleyici sayısı sonradan
   * girilebilir»); başka alan gelirse sunucu 400 diyor.
   */
  updateSession(
    id: string,
    input: {
      topic?: string;
      room?: string | null;
      startsAt?: string | null;
      endsAt?: string | null;
      attendeeCount?: number | null;
      note?: string | null;
      version: number;
    },
  ): Promise<EventSession> {
    return api.patch<EventSession>(`/events/sessions/${id}`, input);
  },
  /**
   * SÜRÜM SORGU DİZESİNDE, GÖVDEDE DEĞİL: `api.delete` gövde taşımıyor ve uç `version`'ı
   * ZORUNLU istiyor (`versionQuery`). İlk yazımda unutuldu ve sonucu ölçüldü: her silme
   * `400 VALIDATION_ERROR {"version":["expected number, received NaN"]}` dönüyordu — yani
   * çöp kutusu düğmesi hiç çalışmıyordu ve kullanıcı «İstek geçersiz» toast'ı görüyordu.
   */
  removeSession(id: string, version: number): Promise<void> {
    return api.delete(`/events/sessions/${id}`, { params: { version: String(version) } });
  },
  addSpeaker(
    sessionId: string,
    input: { physicianId: string; role?: "SPEAKER" | "MODERATOR"; currency?: string; fee?: string },
  ): Promise<EventSession> {
    return api.post<EventSession>(`/events/sessions/${sessionId}/speakers`, input);
  },
  /** Rol sonradan değişiyor (M6); aynı hekim aynı oturumda aynı rolde iki kez olamıyor. */
  updateSpeaker(
    id: string,
    input: { role?: "SPEAKER" | "MODERATOR"; fee?: string | null; version: number },
  ): Promise<EventSession> {
    return api.patch<EventSession>(`/events/speakers/${id}`, input);
  },
  removeSpeaker(id: string, version: number): Promise<void> {
    return api.delete(`/events/speakers/${id}`, { params: { version: String(version) } });
  },

  materials(eventId: string): Promise<EventMaterial[]> {
    return api.get<EventMaterial[]>(`/events/${eventId}/materials`);
  },
  createMaterial(
    eventId: string,
    input: { name: string; sentQuantity?: string; remainingQuantity?: string; note?: string },
  ): Promise<EventMaterial> {
    return api.post<EventMaterial>(`/events/${eventId}/materials`, input);
  },
  updateMaterial(
    id: string,
    input: {
      remainingQuantity?: string | null;
      stockDocumentNo?: string | null;
      note?: string | null;
      version: number;
    },
  ): Promise<EventMaterial> {
    return api.patch<EventMaterial>(`/events/materials/${id}`, input);
  },
  /** Uç vardı, ekranı yoktu (9 Ekim 2026): yanlış girilen materyal kaldırılamıyordu. Açık kongrede. */
  removeMaterial(id: string, version: number): Promise<void> {
    return api.delete(`/events/materials/${id}`, { params: { version: String(version) } });
  },

  /**
   * KONGRE RAPORU ETKİNLİĞİN KENDİSİNDE duruyor (`reportSummary`, `standVisitors`, …), ayrı
   * bir kayıtta değil: bir kongrenin bir raporu var, sürümü yok.
   *
   * AYRI UÇ OLMASININ SEBEBİ İZİN DEĞİL — kapısı `events.event.manage`, `PATCH /events/:id`
   * ile aynı. Ayrı olmasının sebebi KAPALI ETKİNLİĞE DE YAZILABİLMESİ: rapor kongre
   * bittikten SONRA doldurulur, ve `updateReport` bilerek `reachEvent` çağırıyor
   * (`reachOpenEvent` değil). Alan düzeltmesi kapalı etkinlikte reddedilir, rapor edilmez.
   */
  updateReport(
    eventId: string,
    input: {
      reportSummary?: string | null;
      standVisitors?: number | null;
      sessionAttendees?: number | null;
      fieldFeedback?: string | null;
      evaluationNote?: string | null;
      version: number;
    },
  ): Promise<EventRow> {
    return api.patch<EventRow>(`/events/${eventId}/report`, input);
  },

  participants(eventId: string): Promise<EventParticipant[]> {
    return api.get<EventParticipant[]>(`/events/${eventId}/participants`);
  },
  tasks(subjectId: string): Promise<Page<EventTask>> {
    return api.page<EventTask>("/tasks", { params: { subjectType: "event", subjectId, limit: 100 } });
  },
  changeTaskStatus(id: string, input: { status: "OPEN" | "DONE" | "CANCELLED"; version: number }) {
    return api.post<EventTask>(`/tasks/${id}/status`, input);
  },
};

export const eventPlansApi = {
  list(params: { year?: number; limit?: number; legalEntityId?: string }): Promise<Page<EventPlan>> {
    return api.page<EventPlan>("/events/plans", { params });
  },
  create(input: { legalEntityId: string; year: number; note?: string }): Promise<EventPlan> {
    return api.post<EventPlan>("/events/plans", input);
  },
  approve(id: string, input: { version: number; note?: string }): Promise<EventPlan> {
    return api.post<EventPlan>(`/events/plans/${id}/approve`, input);
  },
  /** Planın notu — yalnız taslakta (onaylı plan kilitli; sunucu 409 veriyor). */
  update(id: string, input: { note: string | null; version: number }): Promise<EventPlan> {
    return api.patch<EventPlan>(`/events/plans/${id}`, input);
  },
  /** Planın geçmişi — açılış, not, kongre bağlama/çıkarma, onay (31 Ekim 2026, P-D). */
  history(id: string): Promise<EventPlanHistoryEntry[]> {
    return api.get<EventPlanHistoryEntry[]>(`/events/plans/${id}/history`);
  },
};

/**
 * Hekim listesinin sorgusu — sunucunun kabul ettiği alanlar (`PhysicianController_list`): sıralama
 * dört alanda, şehir ve branş süzgeci (31 Ekim 2026: liste sıralama ve süzgeci göndermiyordu).
 */
// `type`, `interface` değil: istemcinin `params`'ı dizin imzalı bir kayıt istiyor.
export type PhysicianListParams = {
  page?: number;
  limit?: number;
  q?: string;
  city?: string;
  specialty?: string;
  sortBy?: "fullName" | "institution" | "city" | "createdAt";
  sortOrder?: "asc" | "desc";
};

export const physiciansApi = {
  list(params: PhysicianListParams): Promise<Page<Physician>> {
    return api.page<Physician>("/physicians", { params });
  },
  create(input: {
    fullName: string;
    institution?: string;
    city?: string;
    specialty?: string;
  }): Promise<Physician> {
    return api.post<Physician>("/physicians", input);
  },
  /** pz-gorev 6 Ekim 2026: hekim çekmecesi (M11) — kayıt, listede olmayan bir hekim de açılsın. */
  get(id: string): Promise<Physician> {
    return api.get<Physician>(`/physicians/${encodeURIComponent(id)}`);
  },
  /** pz-gorev 6 Ekim 2026: hekim çekmecesinden düzeltme (M11). `null` alanı boşaltır. */
  update(
    id: string,
    input: {
      fullName?: string;
      institution?: string | null;
      city?: string | null;
      specialty?: string | null;
      note?: string | null;
      version: number;
    },
  ): Promise<Physician> {
    return api.patch<Physician>(`/physicians/${encodeURIComponent(id)}`, input);
  },
  /** «Bu hekim zaten kayıtlı olabilir» uyarısı — kayıt açmadan sorulabiliyor. */
  similar(fullName: string) {
    return api.get<{ id: string; fullName: string; institution: string | null; city: string | null }[]>(
      "/physicians/similar",
      { params: { fullName } },
    );
  },
  transferSummary(id: string, year?: number) {
    return api.get<{
      physicianId: string;
      fullName: string;
      year: number | null;
      eventCount: number;
      currency: string;
      participationTotal: string;
      speakerFeeTotal: string;
      others: Array<{ key: string; label: string; total: string; count: number }>;
      grandTotal: string;
      foreignLineCount: number;
    }>(`/physicians/${id}/transfer-summary`, { params: year ? { year } : {} });
  },
};
